import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    NgModuleRef,
    OnDestroy,
    Output,
} from '@angular/core';
import {
    KpiResult,
    KpiResultDataEntryResponsePeriod,
    KpiResultDataEntryResponseTransfer,
    KpiResultPeriod,
} from '@masar/common/models';
import { permissionList } from '@masar/common/constants';
import { Subject } from 'rxjs';
import { ResultAnalysisDialogComponent } from '../result-analysis-dialog/result-analysis-dialog.component';
import { ImprovementProceduresDialogComponent } from '../improvement-procedures-dialog/improvement-procedures-dialog.component';
import { CapabilityListComponent } from '../capability-list/capability-list.component';
import { PeriodFieldHistoryDialogComponent } from '../period-field-history-dialog/period-field-history-dialog.component';
import { takeUntil } from 'rxjs/operators';
import { ApprovalComponent } from '../approval/approval.component';
import { PeriodBreakdownComponent } from '../period-breakdown/period-breakdown.component';
import { ModalService } from 'mnm-webapp';
import { TranslateService } from '@ngx-translate/core';
import { AppSettingFetcherService } from '@masar/core/services';
import { KpiResultAttachmentListComponent } from '../../../helpers';
import { KpiMeasurementCycleType } from '@masar/common/types';

@Component({
    selector: 'app-periods-table',
    templateUrl: './periods-table.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class PeriodsTableComponent implements OnDestroy {
    @Input() public mode!: 'new' | 'edit';

    @Input() public type!: 'kpi-result' | 'data-entry-response';

    @Input() public targetId!: string;

    @Input() public kpiResult!: KpiResult;

    @Input() public periods!:
        KpiResultPeriod[] | KpiResultDataEntryResponsePeriod[];

    @Input() public measurementCycle: KpiMeasurementCycleType;

    @Input() public formula: string;

    @Input() public isSubmitting: boolean = false;

    // Fields Only For Kpi Result
    @Input() public target: number;
    @Input() public units: KpiResult['units'];
    @Input() public unitsDescription: string;
    @Input() public inputModeA: string;
    @Input() public inputModeB: string;

    // Fields Only For Data Entry Response
    @Input()
    public currentTransferAssignee: KpiResultDataEntryResponseTransfer['assignee'];
    @Input() public canEditAnalysisAndImprovement = false;
    @Input() public canViewFieldHistory = false;
    @Input() public readOnly: boolean = false;
    @Input() public actionMode: 'save-and-move' | 'move-and-reject' | 'none' =
        'none';

    @Output() public forceSave = new EventEmitter<void>();

    @Output() public forceRefresh = new EventEmitter<void>();

    public isBreakdownEnabled = false;

    public readonly permissionList = permissionList;

    private unsubscribeAll = new Subject<void>();

    public constructor(
        private readonly modalService: ModalService,
        private readonly moduleRef: NgModuleRef<any>,
        private readonly translateService: TranslateService,
        private readonly cdr: ChangeDetectorRef,
        appSettingFetcherService: AppSettingFetcherService
    ) {
        appSettingFetcherService.get$
            .pipe(takeUntil(this.unsubscribeAll))
            .subscribe(setting => {
                this.isBreakdownEnabled =
                    setting.kpiSetting.isKpiResultValueBreakdownEnabled;
                this.cdr.markForCheck();
            });
    }

    public ngOnDestroy(): void {
        this.unsubscribeAll.next();
        this.unsubscribeAll.complete();
    }

    public get canManageAnalysisAndImprovement(): boolean {
        return this.type === 'kpi-result'
            ? (this.kpiResult?.canEditAnalysisAndImprovement ?? false)
            : this.canEditAnalysisAndImprovement;
    }

    public get isFieldHistoryEnabled(): boolean {
        return this.type === 'data-entry-response'
            ? this.canViewFieldHistory
            : (this.kpiResult?.canViewFieldHistory ?? false);
    }

    public trackByIndex(idx: number): number {
        return idx;
    }

    public async showResultAnalysisDialog(
        period: KpiResultPeriod
    ): Promise<void> {
        const subject = new Subject<void>();
        const component = await this.modalService.show(
            ResultAnalysisDialogComponent,
            {
                title: this.translateService.instant(
                    'translate_result_analysis'
                ),
                beforeInit: c => {
                    c.type = this.type;
                    c.resultAnalysisAr = period.resultAnalysisAr;
                    c.resultAnalysisEn = period.resultAnalysisEn;
                    c.isApproved = period.isApproved;
                    c.canEditResultAnalysis =
                        this.canManageAnalysisAndImprovement;
                    c.readOnly = this.readOnly;
                },
                moduleRef: this.moduleRef,
                onDismiss: () => {
                    subject.next();
                    subject.complete();
                },
            }
        );

        component.itemUpdated.pipe(takeUntil(subject)).subscribe(value => {
            this.modalService.dismiss(component);
            period.resultAnalysisAr = value.ar;
            period.resultAnalysisEn = value.en;
            this.forceSave.emit();
            this.cdr.markForCheck();
        });
    }

    public async showImprovementProceduresDialog(
        period: KpiResultPeriod
    ): Promise<void> {
        const subject = new Subject<void>();
        const component = await this.modalService.show(
            ImprovementProceduresDialogComponent,
            {
                title: this.translateService.instant(
                    'translate_improvement_procedures'
                ),
                beforeInit: c => {
                    c.type = this.type;
                    c.improvementProcedures = {
                        valueAr: period.improvementProcedureAr,
                        valueEn: period.improvementProcedureEn,
                        percentage:
                            period.improvementProcedureCompletionPercentage,
                        expectedCompletionDate:
                            period.improvementProcedureExpectedCompletionDate,
                    };
                    c.isApproved = period.isApproved;
                    c.canEditImprovementProcedure =
                        this.canManageAnalysisAndImprovement;
                    c.readOnly = this.readOnly;
                },
                moduleRef: this.moduleRef,
                onDismiss: () => {
                    subject.next();
                    subject.complete();
                },
            }
        );

        component.itemUpdated.pipe(takeUntil(subject)).subscribe(value => {
            this.modalService.dismiss(component);
            period.improvementProcedureAr = value.valueAr;
            period.improvementProcedureEn = value.valueEn;
            period.improvementProcedureCompletionPercentage = value.percentage;
            period.improvementProcedureExpectedCompletionDate =
                value.expectedCompletionDate;
            this.forceSave.emit();
            this.cdr.markForCheck();
        });
    }

    public async showAttachmentListDialog(
        period: KpiResultPeriod
    ): Promise<void> {
        const subject = new Subject<void>();

        const component = await this.modalService.show(
            KpiResultAttachmentListComponent,
            {
                title: this.translateService.instant('translate_attachments'),
                beforeInit: c => {
                    c.type = this.type;
                    c.targetId = this.targetId;
                    c.period = period;

                    if (this.type === 'data-entry-response') {
                        c.mode =
                            this.actionMode === 'save-and-move'
                                ? 'default'
                                : 'view';
                    } else {
                        c.mode = period.isApproved ? 'view' : 'default';
                    }
                },
                moduleRef: this.moduleRef,
            }
        );

        component.update.pipe(takeUntil(subject)).subscribe(() => {
            this.forceRefresh.emit();
            this.cdr.markForCheck();
        });
    }

    public async showCapabilityListDialog(
        period: KpiResultPeriod
    ): Promise<void> {
        const subject = new Subject<void>();

        const component = await this.modalService.show(
            CapabilityListComponent,
            {
                title: this.translateService.instant('translate_capabilities'),
                beforeInit: c => {
                    c.periodId = period.id;
                    c.type = this.type;

                    if (this.type === 'data-entry-response') {
                        c.mode =
                            this.actionMode === 'save-and-move'
                                ? 'default'
                                : 'view';
                    } else {
                        c.mode = period.isApproved ? 'view' : 'default';
                    }
                },
                moduleRef: this.moduleRef,
            }
        );

        component.update.pipe(takeUntil(subject)).subscribe(() => {
            this.forceRefresh.emit();
            this.cdr.markForCheck();
        });
    }

    public async showApprovalDialog(period: KpiResultPeriod): Promise<void> {
        const subject = new Subject<void>();

        const component = await this.modalService.show(ApprovalComponent, {
            title: this.translateService.instant(
                'translate_approval_and_suggestions'
            ),
            beforeInit: c => {
                c.period = period;
            },
            moduleRef: this.moduleRef,
        });

        component.approved.pipe(takeUntil(subject)).subscribe(() => {
            this.forceRefresh.emit();
            this.cdr.markForCheck();
        });

        component.disapproved.pipe(takeUntil(subject)).subscribe(() => {
            this.forceRefresh.emit();
            this.cdr.markForCheck();
        });
    }

    public async showBreakdownDialog(period: KpiResultPeriod): Promise<void> {
        const subject = new Subject<void>();

        const component = await this.modalService.show(
            PeriodBreakdownComponent,
            {
                title: this.translateService.instant(
                    'translate_period_breakdown'
                ),
                beforeInit: c => {
                    c.period = period;
                },
                moduleRef: this.moduleRef,
            }
        );

        component.update.pipe(takeUntil(subject)).subscribe(() => {
            this.forceRefresh.emit();
            this.cdr.markForCheck();
        });
    }

    public async showFieldHistoryDialog(
        period: KpiResultPeriod
    ): Promise<void> {
        if (!period.id) return;

        await this.modalService.show(PeriodFieldHistoryDialogComponent, {
            title: this.translateService.instant('translate_history'),
            beforeInit: c => {
                c.periodId = period.id;
                c.source = this.type;
            },
            moduleRef: this.moduleRef,
        });
    }
}
